document.addEventListener('DOMContentLoaded', () => {
  try {
    const carousels = document.querySelectorAll('.carousel-container');
    if (!carousels.length) return;

    // ---- Utilitaires --------------------------------------------------------
    const debounce = (fn, ms = 150) => {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
    };

    carousels.forEach((container) => {
      // ---- Viewport & Track -------------------------------------------------
      const viewport = container.querySelector('.carousel-slides');
      if (!viewport) return;

      let track = viewport.querySelector('.carousel-track');
      if (!track) {
        track = document.createElement('div');
        track.className = 'carousel-track';
        while (viewport.firstChild) track.appendChild(viewport.firstChild);
        viewport.appendChild(track);
      }

      const slides = Array.from(track.querySelectorAll('.carousel-slide'));
      if (!slides.length) return;
      const total = slides.length;

      // ---- Data attrs -------------------------------------------------------
      const autoplay      = container.dataset.autoplay === 'true';
      const autoplaySpeed = parseInt(container.dataset.autoplaySpeed || '3000', 10);
      const transition    = container.dataset.transition || 'slide';
      const showArrows    = container.dataset.showArrows === 'true';
      const isBookStyle   = container.classList.contains('style-book');
      const loop          = container.dataset.loop !== 'false'; // activé par défaut

      // ---- État -------------------------------------------------------------
      let current    = 0;
      let intervalId = null;
      let thumbsBuilt = false;
      let isPaused   = false;

      // ---- Thumbs refs ------------------------------------------------------
      const thumbsContainer = container.querySelector('.carousel-thumbs');
      const thumbsPrev      = container.querySelector('.thumbs-prev');
      const thumbsNext      = container.querySelector('.thumbs-next');

      // ---- Accessibility : aria-live ----------------------------------------
      viewport.setAttribute('aria-live', 'polite');
      viewport.setAttribute('aria-atomic', 'false');

      // ---- Suppression du compteur existant s'il y en a un --------------------
      const existingCounter = container.querySelector('.carousel-counter');
      if (existingCounter) existingCounter.remove();

      // ---- Lazy loading -----------------------------------------------------
      const lazyLoad = (() => {
        const loaded = new Set();

        const loadSlide = (slide) => {
          if (loaded.has(slide)) return;
          loaded.add(slide);
          // <img data-src> → src
          slide.querySelectorAll('img[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          });
          // background lazifié via data-bg
          slide.querySelectorAll('[data-bg]').forEach((el) => {
            el.style.backgroundImage = `url(${el.dataset.bg})`;
            el.removeAttribute('data-bg');
          });
        };

        // Charger slide courant + voisins
        const preload = (idx) => {
          loadSlide(slides[idx]);
          if (slides[(idx + 1) % total]) loadSlide(slides[(idx + 1) % total]);
          if (slides[(idx - 1 + total) % total]) loadSlide(slides[(idx - 1 + total) % total]);
        };

        return { preload };
      })();

      // ---- Thumbnails : active state ----------------------------------------
      const setActiveThumb = (idx) => {
        if (!thumbsContainer) return;
        const thumbs = thumbsContainer.querySelectorAll('.carousel-thumb');
        thumbs.forEach((t, i) => {
          const active = i === idx;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', String(active));
          t.setAttribute('tabindex', active ? '0' : '-1');
        });

        const activeThumb = thumbs[idx];
        if (activeThumb) {
          const top     = activeThumb.offsetTop;
          const bottom  = top + activeThumb.offsetHeight;
          const cTop    = thumbsContainer.scrollTop;
          const cBottom = cTop + thumbsContainer.clientHeight;
          if (top < cTop) {
            thumbsContainer.scrollTo({ top: top - 6, behavior: 'smooth' });
          } else if (bottom > cBottom) {
            thumbsContainer.scrollTo({
              top: bottom - thumbsContainer.clientHeight + 6,
              behavior: 'smooth',
            });
          }
        }
      };

      // ---- Book style classes -----------------------------------------------
      const updateClassesBook = () => {
        slides.forEach((s, i) => {
          s.classList.remove('slide-current', 'slide-before', 'slide-after');
          if (i === current)                            s.classList.add('slide-current');
          else if (i === (current - 1 + total) % total) s.classList.add('slide-before');
          else if (i === (current + 1) % total)          s.classList.add('slide-after');
        });
      };

      // ---- Positionnement ---------------------------------------------------
      const applyPositioning = () => {
        // Hauteur explicite depuis le container — évite les problèmes de
        // résolution de height:100% dans les contextes grid/flex imbriqués.
        const h = container.clientHeight || 500;

        viewport.style.overflow  = 'hidden';
        viewport.style.position  = 'relative';
        viewport.style.height    = h + 'px';

        if (isBookStyle) { updateClassesBook(); return; }

        if (transition === 'fade') {
          track.style.display    = 'block';
          track.style.height     = h + 'px';
          track.style.transform  = '';
          track.style.transition = '';
          track.style.position   = 'relative';

          slides.forEach((s, i) => {
            s.style.position   = 'absolute';
            s.style.inset      = '0';
            s.style.height     = h + 'px';
            s.style.opacity    = i === current ? '1' : '0';
            s.style.zIndex     = i === current ? '2' : '1';
            s.style.transition = 'opacity 0.5s ease';
            s.style.flex       = '';
          });
        } else {
          // Chaque slide est positionné en absolu (même point de layout 0,0).
          // overflow:hidden ne clip donc jamais les slides hors-écran en layout.
          // Le mouvement est rendu par translateX sur chaque slide individuellement.
          track.style.display    = 'block';
          track.style.position   = 'relative';
          track.style.height     = h + 'px';
          track.style.width      = '100%';
          track.style.transform  = '';
          track.style.transition = '';

          slides.forEach((s, i) => {
            const offset = i - current;
            s.style.position   = 'absolute';
            s.style.top        = '0';
            s.style.left       = '0';
            s.style.width      = '100%';
            s.style.height     = h + 'px';
            s.style.flex       = '';
            s.style.inset      = '';
            s.style.opacity    = '';
            s.style.zIndex     = String(i === current ? 2 : 1);
            s.style.transition = 'transform 0.5s ease-in-out';
            s.style.transform  = `translateX(${offset * 100}%)`;
          });
        }
      };

      // ---- showSlide --------------------------------------------------------
      const showSlide = (idx) => {
        if (!loop) {
          current = Math.max(0, Math.min(idx, total - 1));
        } else {
          current = (idx + total) % total;
        }
        applyPositioning();
        setActiveThumb(current);
        lazyLoad.preload(current);

        // ARIA courant
        slides.forEach((s, i) => {
          s.setAttribute('aria-hidden', String(i !== current));
          if (i === current) s.setAttribute('aria-roledescription', 'slide');
        });
      };

      const nextSlide = () => showSlide(current + 1);
      const prevSlide = () => showSlide(current - 1);

      // ---- Thumbnails build (une seule fois) --------------------------------
      const extractImageURL = (slide) => {
        let bg = slide.style.backgroundImage;
        if (!bg || bg === 'none') {
          const media = slide.querySelector('.carousel-media');
          if (media) bg = media.style.backgroundImage || getComputedStyle(media).backgroundImage;
        }
        if (bg && bg !== 'none') {
          const m = bg.match(/url\(["']?(.*?)["']?\)/);
          if (m && m[1]) return m[1];
        }
        const img = slide.querySelector('img');
        return img && img.src ? img.src : '';
      };

      const thumbStep = () => {
        if (!thumbsContainer) return 0;
        const first = thumbsContainer.querySelector('.carousel-thumb');
        if (!first) return 0;
        const cs    = getComputedStyle(thumbsContainer);
        const gap   = parseFloat(cs.rowGap || cs.gap || '0') || 0;
        const h     = first.getBoundingClientRect().height;
        return h + gap;
      };

      const buildThumbsOnce = () => {
        if (!thumbsContainer || thumbsBuilt) return;
        thumbsBuilt = true;
        thumbsContainer.innerHTML = '';
        thumbsContainer.setAttribute('role', 'tablist');
        thumbsContainer.setAttribute('aria-label', 'Slide thumbnails');

        // Assurer le scroll vertical sur le conteneur
        thumbsContainer.style.overflowY = 'auto';
        thumbsContainer.style.overflowX = 'hidden';
        thumbsContainer.style.scrollBehavior = 'smooth';

        slides.forEach((slide, i) => {
          const url = extractImageURL(slide);
          const btn = document.createElement('button');
          btn.type      = 'button';
          btn.className = 'carousel-thumb';
          btn.setAttribute('role', 'tab');
          btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
          btn.setAttribute('aria-selected', 'false');
          btn.setAttribute('tabindex', '-1');

          // Forcer les styles inline pour garantir la taille
          btn.style.cssText = [
            'display:block',
            'width:100%',
            'min-height:80px',
            'aspect-ratio:3/2',
            'background-color:transparent',
            'background-size:cover',
            'background-position:center',
            'background-repeat:no-repeat',
            'border:2px solid transparent',
            'border-radius:6px',
            'padding:0',
            'margin:0',
            'cursor:pointer',
            'flex-shrink:0',
            'box-sizing:border-box',
            '-webkit-appearance:none',
            'appearance:none',
          ].join(';');
          if (url) btn.style.backgroundImage = `url(${url})`;

          btn.addEventListener('click', () => showSlide(i));
          thumbsContainer.appendChild(btn);
        });

        // Scroll arrows
        if (thumbsPrev) {
          thumbsPrev.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const step = thumbStep();
            if (step > 0) {
              thumbsContainer.scrollTop = Math.max(0, thumbsContainer.scrollTop - step);
            }
          });
        }
        if (thumbsNext) {
          thumbsNext.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const step = thumbStep();
            if (step > 0) {
              thumbsContainer.scrollTop = Math.min(
                thumbsContainer.scrollHeight - thumbsContainer.clientHeight,
                thumbsContainer.scrollTop + step
              );
            }
          });
        }
      };

      const setThumbsViewport = () => {
        // La hauteur est gérée par CSS flex (le conteneur prend
        // tout l'espace entre les boutons prev/next).
        // On s'assure juste que overflow est bien défini.
        if (!thumbsContainer) return;
        thumbsContainer.style.overflowY = 'auto';
      };

      // ---- Arrows -----------------------------------------------------------
      if (showArrows && !isBookStyle) {
        let nav = container.querySelector('.carousel-navigation');
        if (!nav) {
          nav = document.createElement('div');
          nav.className = 'carousel-navigation';
          container.appendChild(nav);
        }
        let prevBtn = nav.querySelector('.prev') || nav.querySelector('.carousel-prev');
        let nextBtn = nav.querySelector('.next') || nav.querySelector('.carousel-next');

        if (!prevBtn) {
          prevBtn = document.createElement('button');
          prevBtn.className = 'prev';
          prevBtn.setAttribute('aria-label', 'Previous slide');
          prevBtn.innerHTML = '‹';
          nav.appendChild(prevBtn);
        }
        if (!nextBtn) {
          nextBtn = document.createElement('button');
          nextBtn.className = 'next';
          nextBtn.setAttribute('aria-label', 'Next slide');
          nextBtn.innerHTML = '›';
          nav.appendChild(nextBtn);
        }
        prevBtn.addEventListener('click', prevSlide);
        nextBtn.addEventListener('click', nextSlide);
      }

      // ---- Book DOM ensure --------------------------------------------------
      const ensureBookDom = () => {
        if (!isBookStyle) return;
        slides.forEach((slide) => {
          let card = slide.querySelector('.carousel-card');
          if (!card) {
            card = document.createElement('div');
            card.className = 'carousel-card';
            const media   = slide.querySelector('.carousel-media');
            const caption = slide.querySelector('.carousel-caption');
            if (media) card.appendChild(media);
            if (caption) card.appendChild(caption);
            slide.appendChild(card);
          }
          let tab = slide.querySelector('.carousel-tab');
          if (!tab) {
            const titleText =
              (slide.querySelector('.carousel-caption h3')?.textContent || '').trim() ||
              slide.getAttribute('data-title') || ' ';
            tab = document.createElement('div');
            tab.className = 'carousel-tab';
            const span = document.createElement('span');
            span.className = 'carousel-tab__label';
            span.textContent = titleText;
            tab.appendChild(span);
            slide.insertBefore(tab, slide.firstChild);
          }
        });
      };

      // ---- Fullscreen -------------------------------------------------------
      const fsBtn = container.querySelector('.carousel-fullscreen');
      if (fsBtn) {
        fsBtn.addEventListener('click', () => {
          if (!document.fullscreenElement) {
            (container.requestFullscreen ||
             container.webkitRequestFullscreen ||
             container.msRequestFullscreen
            )?.call(container);
            container.classList.add('is-fullscreen');
          } else {
            (document.exitFullscreen ||
             document.webkitExitFullscreen ||
             document.msExitFullscreen
            )?.call(document);
            container.classList.remove('is-fullscreen');
          }
        });
        document.addEventListener('fullscreenchange', () => {
          if (!document.fullscreenElement) container.classList.remove('is-fullscreen');
        });
      }

      // ---- Keyboard ---------------------------------------------------------
      container.setAttribute('tabindex', '0');
      container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); prevSlide(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); nextSlide(); }
        if (e.key === 'Escape' && document.fullscreenElement) {
          document.exitFullscreen();
        }
      });

      // ---- Click on slides (book: open tab) ---------------------------------
      slides.forEach((s, i) => {
        if (s.__clickBound) return;
        s.__clickBound = true;
        s.addEventListener('click', (e) => {
          const link = e.target && e.target.closest('a');
          if (i !== current) {
            if (link) e.preventDefault();
            showSlide(i);
          }
        });
      });

      // ---- Autoplay / Pause -------------------------------------------------
      const startAutoplay = () => {
        if (!intervalId && !isPaused) intervalId = setInterval(nextSlide, autoplaySpeed);
      };
      const stopAutoplay = () => {
        if (intervalId) clearInterval(intervalId);
        intervalId = null;
      };

      // Pause on hover
      container.addEventListener('mouseenter', () => { isPaused = true; stopAutoplay(); });
      container.addEventListener('mouseleave', () => { isPaused = false; if (autoplay) startAutoplay(); });

      // Pause on focus (accessibilité)
      container.addEventListener('focusin',  () => { isPaused = true; stopAutoplay(); });
      container.addEventListener('focusout', (e) => {
        if (!container.contains(e.relatedTarget)) {
          isPaused = false;
          if (autoplay) startAutoplay();
        }
      });

      // ---- Swipe (touch) ----------------------------------------------------
      let startX = 0, endX = 0;
      container.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX; endX = startX;
      }, { passive: true });
      container.addEventListener('touchmove', (e) => {
        endX = e.touches[0].clientX;
      }, { passive: true });
      container.addEventListener('touchend', () => {
        const dx = startX - endX;
        if (Math.abs(dx) > 50) { dx > 0 ? nextSlide() : prevSlide(); }
      });

      // ---- Init -------------------------------------------------------------
      ensureBookDom();

      // Fix aria-hidden sur le conteneur de thumbnails : aria-hidden bloque
      // le focus des boutons enfants. On le retire et on gère la visibilité
      // screen-reader via les attributs aria sur chaque thumb individuellement.
      const thumbsWrap = container.querySelector('.carousel-thumbs-wrap');
      if (thumbsWrap) thumbsWrap.removeAttribute('aria-hidden');

      buildThumbsOnce();
      showSlide(0);
      setThumbsViewport();
      if (autoplay) startAutoplay();

      // ---- Resize (debounced, UNE SEULE attache) ----------------------------
      const onResize = debounce(() => {
        applyPositioning();
        setThumbsViewport();
      }, 150);
      window.addEventListener('resize', onResize);

      // Reflow au chargement des images
      track.querySelectorAll('img').forEach((img) =>
        img.addEventListener('load', () => {
          if (transition === 'slide') applyPositioning();
        })
      );
    });
  } catch (e) {
    console.error('Error in carousel view.js:', e);
  }
});